import axios from 'axios';

// Rassini Control Center — planning API client (tech-spec Rev C §4).
// Every route under /machinesim/api/planning is auth-gated (MM Bearer token validated
// server-side against /accounts/current). Callers pass the accessToken from useMMAuth();
// in local no-auth dev the token is null and these calls will 401 — that is expected
// until the app runs behind MMProvider (see main.jsx / default.json).

const API_BASE = '/machinesim/api/planning';

const auth = (token) => (token ? { headers: { Authorization: `Bearer ${token}` } } : {});

// ── Templates ────────────────────────────────────────────────────────────────
export const listTemplates = async (companySlug, token) => {
  const res = await axios.get(`${API_BASE}/companies/${companySlug}/scenario-templates`, auth(token));
  return res.data?.templates ?? [];
};

// ── Generate a scenario from a template (also auto-builds plan v1) ────────────
// body: { seed?, periodIndex? } → { scenarioId, planId, version }
export const generateScenario = async (templateId, { seed, periodIndex, demandOverride, policy } = {}, token) => {
  const body = {};
  if (seed !== undefined && seed !== null && seed !== '') body.seed = Number(seed);
  if (periodIndex !== undefined) body.periodIndex = Number(periodIndex);
  if (Array.isArray(demandOverride)) body.demandOverride = demandOverride;
  if (policy) body.policy = policy;
  const res = await axios.post(`${API_BASE}/scenario-templates/${templateId}/generate`, body, auth(token));
  return res.data;
};

// ── One-click dispatch: generate → auto-plan → commit → planned mode (CONTRACT §1)
// body: { demandOverride?, company?, policy? } → { scenarioId, planId, plannedMode, committed }
// policy: { lotSizing?, transfer? (incl. byStage per-edge overrides), dispatch? } overrides template defaults.
export const dispatchScenario = async (templateId, { demandOverride, company, policy } = {}, token) => {
  const body = {};
  if (Array.isArray(demandOverride)) body.demandOverride = demandOverride;
  if (company) body.company = company;
  if (policy) body.policy = policy;
  const res = await axios.post(`${API_BASE}/scenario-templates/${templateId}/dispatch`, body, auth(token));
  return res.data; // { scenarioId, planId, plannedMode, committed:{ workOrders, operations, assignments[] } }
};

// ── Scenario detail: frozen demand + problemSnapshot (roster) + plan versions ─
export const getScenario = async (scenarioId, token) => {
  const res = await axios.get(`${API_BASE}/scenarios/${scenarioId}`, auth(token));
  return res.data?.scenario ?? null;
};

// ── Pure projection: { capacity, schedule, flow } (no execution writes) ──────
export const getProjection = async (planId, token) => {
  const res = await axios.get(`${API_BASE}/plans/${planId}/projection`, auth(token));
  return res.data; // { planId, capacity, schedule, flow }
};

// ── Build a fresh auto-plan version for a scenario ───────────────────────────
export const buildAutoPlan = async (scenarioId, token) => {
  const res = await axios.post(`${API_BASE}/scenarios/${scenarioId}/plans`, {}, auth(token));
  return res.data; // { planId, version, source }
};

// ── Fork a DRAFT into a new editable MANUAL version (checkpoint) ─────────────
export const checkpointPlan = async (planId, token) => {
  const res = await axios.post(`${API_BASE}/plans/${planId}/checkpoint`, {}, auth(token));
  return res.data; // { planId, version }
};

// ── Mutate a DRAFT plan; returns the fresh projection ────────────────────────
// edit: { op:'resizeLot'|'reassignOp'|'reorderOp'|'holdOp'|'setPriority', ... }
export const patchPlan = async (planId, edit, token) => {
  const res = await axios.patch(`${API_BASE}/plans/${planId}`, { edit }, auth(token));
  return res.data; // { planId, capacity, schedule, flow }
};

// ── Commit a plan → compile into WorkOrders (execution) ──────────────────────
export const commitPlan = async (scenarioId, planId, token) => {
  const res = await axios.post(`${API_BASE}/scenarios/${scenarioId}/plans/${planId}/commit`, {}, auth(token));
  return res.data; // { committed, workOrders }
};
